import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Loader2, Mail, Lock, User, Phone, Gift, ArrowLeft,
    ShieldCheck, Heart, Sparkles, Copy, Check, CreditCard,
    ClipboardCheck, Clock, Heart as HeartIcon, Ticket, Percent
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { publicAuthApi } from "@/lib/api/provider";
import { directoryApi, membersApi } from "@/lib/api/directory";
import { tokenStore, formatApiErrorDetail } from "@/lib/api";
import { iconForCategory } from "@/lib/categoryIcons";
import LanguageToggle from "@/components/layout/LanguageToggle";

const TOTAL_STEPS = 6;

// ── Bank details for the payment instructions step ──
// Update these with your actual bank account information.
const BANK_DETAILS = {
    accountName: "Hemaia Health Network",
    iban:        "EG000000000000000000000000000",  // ← replace with real IBAN
    bankName:    "CIB – Commercial International Bank",
};

const FEATURES = [
    { icon: ShieldCheck, en: "Trusted network",   ar: "شبكة موثوقة"   },
    { icon: Heart,       en: "Real healthcare",   ar: "رعاية حقيقية"  },
    { icon: Sparkles,    en: "Exclusive savings", ar: "خصومات حصرية"  },
];

const STATS = [
    { value: "500+", label: "Partner clinics" },
    { value: "50K+", label: "Active members"  },
    { value: "30%",  label: "Avg. savings"    },
];

// ── Step indicator ──────────────────────────────────────────────────────────
function StepDots({ current, total = TOTAL_STEPS }) {
    return (
        <div className="flex items-center gap-2 mb-6">
            {Array.from({ length: total }).map((_, i) => (
                <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === current ? "w-6 bg-primary" : i < current ? "w-3 bg-primary/40" : "w-3 bg-border"
                    }`}
                />
            ))}
        </div>
    );
}

// ── Left panel pitch (static across all steps) ─────────────────────────────
const HERO_IMAGE =
    "https://images.pexels.com/photos/1648377/pexels-photo-1648377.jpeg?auto=compress&cs=tinysrgb&w=1400";

function LeftPanel({ lang }) {
    return (
        <div className="hidden md:flex relative flex-1 overflow-hidden">
            <img
                src={HERO_IMAGE}
                alt="Healthcare"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />
            <div className="absolute bottom-0 start-0 end-0 p-10 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-4">
                    {lang === "ar" ? "حماية — شبكة الولاء الصحي في مصر" : "Hemaia — Egypt's Healthcare Loyalty Network"}
                </p>
                <h2 className="text-3xl lg:text-4xl font-bold leading-tight">
                    {lang === "ar" ? "تخطَّ الانتظار\nلا الرعاية." : "Skip the Wait,\nNot the Care."}
                </h2>
                <p className="mt-3 text-sm text-white/65 max-w-sm leading-relaxed">
                    {lang === "ar"
                        ? "وصول فوري إلى أسعار خاصة وخصومات حصرية في مئات المستشفيات والعيادات."
                        : "Instant access to pre-negotiated rates and exclusive discounts at 500+ clinics and hospitals."}
                </p>
                <div className="flex flex-wrap gap-2 mt-5">
                    {FEATURES.map(({ icon: Icon, en, ar }) => (
                        <div
                            key={en}
                            className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3.5 py-1.5 text-white text-xs font-medium"
                        >
                            <Icon className="h-3.5 w-3.5 text-white/80 shrink-0" />
                            {lang === "ar" ? ar : en}
                        </div>
                    ))}
                </div>
                <div className="flex gap-8 mt-6 pt-6 border-t border-white/15">
                    {STATS.map(({ value, label }) => (
                        <div key={label}>
                            <p className="text-2xl font-bold">{value}</p>
                            <p className="text-xs text-white/55 mt-0.5">{label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Step 0: Account creation ────────────────────────────────────────────────
function Step0Account({ lang, refCode, onSuccess }) {
    const [form, setForm]         = useState({
        full_name: "", email: "", phone: "", password: "", ref_code: refCode || "",
    });
    const [submitting, setSubmit] = useState(false);
    const [error, setError]       = useState("");
    const { refresh }             = useAuth();
    const refPrefilled            = !!refCode;

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmit(true);
        try {
            const code = form.ref_code.trim().toUpperCase();
            const payload = {
                full_name: form.full_name.trim(),
                email:     form.email.trim().toLowerCase(),
                phone:     form.phone.trim() || null,
                password:  form.password,
            };
            if (code) payload.ref_code = code;
            const res = await publicAuthApi.signup(payload);
            tokenStore.set(res.access_token, res.refresh_token);
            localStorage.removeItem("ref_code");
            await refresh();
            onSuccess(res);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmit(false); }
    };

    return (
        <>
            <StepDots current={0} />
            <div className="mb-6">
                <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10 mb-7" />
                <h1 className="text-2xl font-semibold tracking-tight">
                    {lang === "ar" ? "أنشئ ملفك الشخصي" : "Create your Member Profile."}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar"
                        ? "سجّل في ثوانٍ واستمتع برعاية صحية أفضل."
                        : "Sign up in seconds and start enjoying better healthcare."}
                </p>
            </div>

            <form onSubmit={submit} className="space-y-4" noValidate>
                <div>
                    <Label htmlFor="su_name">{lang === "ar" ? "الاسم الكامل" : "Full Name"}</Label>
                    <div className="relative mt-1.5">
                        <User className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input id="su_name" required autoFocus value={form.full_name}
                            onChange={(e) => update({ full_name: e.target.value })} className="ps-10" />
                    </div>
                </div>

                <div>
                    <Label htmlFor="su_email">{lang === "ar" ? "البريد الإلكتروني" : "Email"}</Label>
                    <div className="relative mt-1.5">
                        <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input id="su_email" type="email" required dir="ltr" value={form.email}
                            onChange={(e) => update({ email: e.target.value })} className="ps-10" />
                    </div>
                </div>

                <div>
                    <Label htmlFor="su_phone">
                        {lang === "ar" ? "رقم الهاتف" : "Phone"}{" "}
                        <span className="text-muted-foreground font-normal">
                            ({lang === "ar" ? "اختياري" : "optional"})
                        </span>
                    </Label>
                    <div className="relative mt-1.5">
                        <Phone className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input id="su_phone" dir="ltr" value={form.phone}
                            onChange={(e) => update({ phone: e.target.value })}
                            className="ps-10" placeholder="+20…" />
                    </div>
                </div>

                <div>
                    <Label htmlFor="su_pwd">{lang === "ar" ? "كلمة المرور" : "Password"}</Label>
                    <div className="relative mt-1.5">
                        <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input id="su_pwd" type="password" required minLength={6} value={form.password}
                            onChange={(e) => update({ password: e.target.value })} className="ps-10" />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {lang === "ar" ? "٦ أحرف على الأقل" : "At least 6 characters"}
                    </p>
                </div>

                <div>
                    <Label htmlFor="su_ref">
                        {lang === "ar" ? "كود الإحالة" : "Referral code"}{" "}
                        <span className="text-muted-foreground font-normal">
                            ({lang === "ar" ? "اختياري" : "optional"})
                        </span>
                    </Label>
                    <div className="relative mt-1.5">
                        <Gift className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                            id="su_ref"
                            dir="ltr"
                            value={form.ref_code}
                            onChange={(e) => update({ ref_code: e.target.value.toUpperCase() })}
                            placeholder={lang === "ar" ? "مثال: ML-ABC123" : "e.g. ML-ABC123"}
                            className="ps-10"
                            data-testid="signup-ref-code"
                        />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {refPrefilled
                            ? (lang === "ar"
                                ? "تم ملؤه تلقائياً من رابط الدعوة الخاص بك"
                                : "Pre-filled from your invite link")
                            : (lang === "ar"
                                ? "هل أحالك صديق أو مندوب؟ أدخل الكود لتحصل على مكافأة."
                                : "Got a code from a friend or agent? Enter it to claim a bonus.")}
                    </p>
                </div>

                {error && (
                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {lang === "ar" ? "إنشاء الحساب" : "Create Account"}
                </button>
            </form>

            <div className="mt-6 pt-6 border-t border-border/50 text-center">
                <p className="text-sm text-muted-foreground">
                    {lang === "ar" ? "لديك حساب؟ " : "Already have an account? "}
                    <a href="/login" className="text-primary font-semibold hover:underline underline-offset-2">
                        {lang === "ar" ? "تسجيل الدخول" : "Sign In"}
                    </a>
                </p>
            </div>
        </>
    );
}

// ── Step 1: Membership identity ─────────────────────────────────────────────
function Step1Identity({ lang, memberCode, onNext }) {
    const [form, setForm]         = useState({ dob: "", gender: "", national_id: "" });
    const [submitting, setSubmit] = useState(false);
    const [error, setError]       = useState("");

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmit(true);
        try {
            await membersApi.patchIdentity({
                dob:        form.dob || null,
                gender:     form.gender || null,
                national_id: form.national_id.trim() || null,
            });
            onNext();
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmit(false); }
    };

    return (
        <>
            <StepDots current={1} />
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight">
                    {lang === "ar" ? "تفاصيل العضوية" : "Membership Identity"}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar"
                        ? "تحتاجها مراكز الرعاية الصحية للتحقق من هويتك."
                        : "Required by healthcare providers to verify your identity."}
                </p>
            </div>

            {/* Preview card */}
            <div className="rounded-2xl bg-gradient-to-br from-[#012255] to-[#0a3a7a] p-5 text-white mb-6 shadow-lg">
                <p className="text-xs text-white/50 uppercase tracking-widest mb-1">
                    {lang === "ar" ? "بطاقة عضو حماية" : "Hemaia Member Card"}
                </p>
                <p className="text-lg font-bold tracking-wide">{memberCode || "ML-XXXXXX"}</p>
                <p className="text-sm text-white/70 mt-2">
                    {form.dob
                        ? new Date(form.dob).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { day: "numeric", month: "short", year: "numeric" })
                        : (lang === "ar" ? "تاريخ الميلاد" : "Date of Birth")}
                </p>
                <div className="mt-3 inline-block rounded-full border border-white/25 bg-white/10 px-2.5 py-0.5 text-xs">
                    {lang === "ar" ? "جاهز للتفعيل" : "Ready for Activation"}
                </div>
            </div>

            <form onSubmit={submit} className="space-y-4" noValidate>
                <div>
                    <Label htmlFor="dob">{lang === "ar" ? "تاريخ الميلاد" : "Date of Birth"}</Label>
                    <Input id="dob" type="date" className="mt-1.5" dir="ltr"
                        value={form.dob} onChange={(e) => update({ dob: e.target.value })} />
                </div>

                <div>
                    <Label htmlFor="gender">{lang === "ar" ? "الجنس" : "Gender"}</Label>
                    <select
                        id="gender"
                        className="mt-1.5 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        value={form.gender}
                        onChange={(e) => update({ gender: e.target.value })}
                    >
                        <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                        <option value="MALE">{lang === "ar" ? "ذكر" : "Male"}</option>
                        <option value="FEMALE">{lang === "ar" ? "أنثى" : "Female"}</option>
                        <option value="OTHER">{lang === "ar" ? "أخرى" : "Other"}</option>
                    </select>
                </div>

                <div>
                    <Label htmlFor="national_id">
                        {lang === "ar" ? "رقم الهوية الوطنية / جواز السفر" : "National ID / Passport No."}
                        {" "}<span className="text-muted-foreground font-normal">({lang === "ar" ? "اختياري" : "optional"})</span>
                    </Label>
                    <Input id="national_id" dir="ltr" className="mt-1.5" maxLength={30}
                        value={form.national_id} onChange={(e) => update({ national_id: e.target.value })} />
                </div>

                {error && (
                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {lang === "ar" ? "التالي" : "Continue"}
                </button>
            </form>
        </>
    );
}

// ── Step 2: Interests (NEW) ─────────────────────────────────────────────────
function Step2Interests({ lang, onNext, onSkip }) {
    const [categories, setCategories] = useState(null);
    const [selected, setSelected]     = useState([]);
    const [submitting, setSubmit]     = useState(false);
    const [error, setError]           = useState("");

    useEffect(() => {
        let active = true;
        directoryApi.topCategories()
            .then((items) => { if (active) setCategories(items || []); })
            .catch(() => { if (active) setCategories([]); });
        return () => { active = false; };
    }, []);

    const toggle = (id) => {
        setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const submit = async () => {
        setError(""); setSubmit(true);
        try {
            await membersApi.setInterests(selected);
            onNext();
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmit(false); }
    };

    return (
        <>
            <StepDots current={2} />
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight">
                    {lang === "ar" ? "ما الذي يهمك؟" : "What are you interested in?"}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar"
                        ? "اختر الاختصاصات التي تهمك حتى نقترح أفضل العروض لك."
                        : "Pick the specialties you care about so we can surface the best deals for you."}
                </p>
            </div>

            {!categories && (
                <div className="flex justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
            )}

            {categories && categories.length > 0 && (
                <div className="grid grid-cols-2 gap-2.5 mb-5">
                    {categories.map((cat) => {
                        const Icon = iconForCategory(cat.name_en);
                        const isOn = selected.includes(cat.id);
                        const label = lang === "ar" ? cat.name_ar : cat.name_en;
                        return (
                            <button
                                type="button"
                                key={cat.id}
                                onClick={() => toggle(cat.id)}
                                className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm text-start transition-all ${
                                    isOn
                                        ? "border-primary bg-primary/5 text-foreground shadow-sm"
                                        : "border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                }`}
                                data-testid={`interest-${cat.id}`}
                            >
                                <span className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg ${isOn ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                                    <Icon className="h-4 w-4" />
                                </span>
                                <span className="flex-1 truncate font-medium">{label}</span>
                                {isOn && <Check className="h-4 w-4 text-primary shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            )}

            {error && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive mb-3">
                    {error}
                </div>
            )}

            <button
                type="button"
                disabled={submitting}
                onClick={submit}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {selected.length === 0
                    ? (lang === "ar" ? "متابعة" : "Continue")
                    : (lang === "ar" ? `حفظ (${selected.length}) ومتابعة` : `Save ${selected.length} & Continue`)}
            </button>

            <button
                type="button"
                onClick={onSkip}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
                {lang === "ar" ? "تخطّي الآن" : "Skip for now"}
            </button>
        </>
    );
}

// ── Step 3: Payment instructions ────────────────────────────────────────────
function Step3Payment({ lang, onNext }) {
    const [copied, setCopied] = useState(false);

    const copyIban = () => {
        navigator.clipboard.writeText(BANK_DETAILS.iban).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <>
            <StepDots current={3} />
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight">
                    {lang === "ar" ? "تعليمات الدفع" : "Payment Instructions"}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar"
                        ? "حوّل رسوم الاشتراك إلى الحساب التالي لتفعيل عضويتك."
                        : "Transfer your membership fee to the account below to activate your card."}
                </p>
            </div>

            <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-5 space-y-4 mb-6">
                <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                        {lang === "ar" ? "اسم الحساب" : "Account Name"}
                    </p>
                    <p className="font-semibold text-foreground">{BANK_DETAILS.accountName}</p>
                </div>
                <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                        {lang === "ar" ? "البنك" : "Bank"}
                    </p>
                    <p className="font-semibold text-foreground">{BANK_DETAILS.bankName}</p>
                </div>
                <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">IBAN</p>
                    <div className="flex items-center gap-2">
                        <p className="font-mono font-semibold text-foreground text-sm tracking-wider flex-1 break-all" dir="ltr">
                            {BANK_DETAILS.iban}
                        </p>
                        <button
                            type="button"
                            onClick={copyIban}
                            className="shrink-0 flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-accent transition-colors"
                        >
                            {copied
                                ? <><Check className="h-3.5 w-3.5 text-success" />{lang === "ar" ? "تم النسخ" : "Copied"}</>
                                : <><Copy className="h-3.5 w-3.5" />{lang === "ar" ? "نسخ" : "Copy"}</>
                            }
                        </button>
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-amber-400/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 mb-6">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-1">
                    {lang === "ar" ? "مهم جداً" : "Important"}
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-500 leading-relaxed">
                    {lang === "ar"
                        ? "اكتب اسمك أو رقم هاتفك في خانة ملاحظات التحويل حتى نتمكن من التحقق منك."
                        : 'Include your name or mobile number in the transfer notes/reference so we can identify your payment.'}
                </p>
            </div>

            <Button onClick={onNext} className="w-full rounded-xl" size="lg">
                {lang === "ar" ? "لقد أرسلت التحويل" : "I've Made the Transfer"}
            </Button>
        </>
    );
}

// ── Step 4: Submit payment reference ────────────────────────────────────────
function Step4Reference({ lang, onSuccess }) {
    const [ref, setRef]           = useState("");
    const [submitting, setSubmit] = useState(false);
    const [error, setError]       = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!ref.trim()) { setError(lang === "ar" ? "أدخل رقم المرجع" : "Please enter your reference number"); return; }
        setError(""); setSubmit(true);
        try {
            await membersApi.submitPaymentReference(ref.trim());
            onSuccess(ref.trim());
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmit(false); }
    };

    return (
        <>
            <StepDots current={4} />
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight">
                    {lang === "ar" ? "تأكيد الدفع" : "Payment Confirmation"}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar"
                        ? "أدخل رقم العملية أو المرجع من إيصال التحويل البنكي."
                        : "Enter the transaction ID or reference from your bank transfer receipt."}
                </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
                <div>
                    <Label htmlFor="pay_ref">
                        {lang === "ar" ? "رقم المرجع / رقم العملية" : "Payment Reference / Transaction ID"}
                    </Label>
                    <div className="relative mt-1.5">
                        <CreditCard className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                            id="pay_ref"
                            dir="ltr"
                            autoFocus
                            placeholder={lang === "ar" ? "مثال: TXN-12345678" : "e.g. TXN-12345678"}
                            value={ref}
                            onChange={(e) => setRef(e.target.value)}
                            className="ps-10"
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {lang === "ar" ? "إرسال للمراجعة" : "Submit for Verification"}
                </button>
            </form>

            <div className="mt-5 rounded-xl border border-border/50 bg-muted/30 px-4 py-3">
                <div className="flex items-start gap-3">
                    <ClipboardCheck className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        {lang === "ar"
                            ? "لا تقلق إذا لم يكن لديك رقم مرجع؛ يمكنك إرسال اسمك ورقم هاتفك فقط."
                            : "No reference number? You can submit your name and mobile number instead."}
                    </p>
                </div>
            </div>
        </>
    );
}

// ── Step 5: Pick your first deal ─────────────────────────────────────────────
function Step5FirstDeal({ lang, onDone }) {
    const [offers, setOffers] = useState(null);
    const [matched, setMatched] = useState(false);
    const [claimingId, setClaimingId] = useState(null);
    const [claimedId, setClaimedId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                let res = await membersApi.listOffers({ matched_only: true, limit: 6 });
                let didMatch = !!res?.matched_to_interests && (res?.items || []).length > 0;
                if (!didMatch) {
                    res = await membersApi.listOffers({ limit: 6 });
                }
                if (active) {
                    setOffers((res?.items || []).slice(0, 6));
                    setMatched(didMatch);
                }
            } catch (err) {
                if (active) {
                    setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
                    setOffers([]);
                }
            }
        })();
        return () => { active = false; };
    }, []);

    const claim = async (offerId) => {
        setError(""); setClaimingId(offerId);
        try {
            await membersApi.claimOffer(offerId);
            setClaimedId(offerId);
            // Brief moment so the user sees the success state
            setTimeout(() => onDone(), 900);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setClaimingId(null);
        }
    };

    return (
        <>
            <StepDots current={5} />
            <div className="mb-5">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full mb-3">
                    <Check className="h-3.5 w-3.5" />
                    {lang === "ar" ? "تم استلام الدفع" : "Payment received"}
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">
                    {lang === "ar" ? "احجز أول عرض" : "Claim your first deal"}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {matched
                        ? (lang === "ar"
                            ? "إليك أفضل العروض من الاختصاصات التي اخترتها."
                            : "Here are the best deals in the specialties you picked.")
                        : (lang === "ar"
                            ? "إليك أفضل العروض المتاحة الآن."
                            : "Here are the top deals available right now.")}
                </p>
            </div>

            {offers === null && (
                <div className="flex justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
            )}

            {offers && offers.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-center mb-4">
                    <Ticket className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                        {lang === "ar" ? "لا توجد عروض في الوقت الحالي." : "No deals available right now."}
                    </p>
                </div>
            )}

            {offers && offers.length > 0 && (
                <div className="space-y-2.5 mb-4 max-h-[440px] overflow-y-auto pe-1">
                    {offers.map((o) => {
                        const title = lang === "ar" ? (o.title_ar || o.title_en) : (o.title_en || o.title_ar);
                        const isClaimed = claimedId === o.id;
                        const isClaiming = claimingId === o.id;
                        const isPercentage = o.discount_type === "PERCENTAGE";
                        const estSavings = o.original_price && isPercentage
                            ? Math.round(o.original_price * o.discount_value / 100)
                            : (o.original_price ? o.original_price - o.discount_value : null);
                        return (
                            <div key={o.id} className={`rounded-xl border p-3 flex items-center gap-3 transition-colors ${isClaimed ? "border-emerald-300 bg-emerald-50/50" : "border-border bg-card"}`}>
                                <div className="h-14 w-14 rounded-lg bg-secondary shrink-0 overflow-hidden">
                                    {o.image_url ? (
                                        <img src={o.image_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Percent className="h-5 w-5 text-primary/40" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold truncate">{title}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {o.provider_name}
                                        {estSavings ? (
                                            <>
                                                <span className="mx-1.5">•</span>
                                                <span className="text-emerald-700 font-medium">
                                                    {lang === "ar" ? `وفر ~${estSavings} ج.م` : `Save ~${estSavings} EGP`}
                                                </span>
                                            </>
                                        ) : null}
                                    </p>
                                </div>
                                {isClaimed ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 px-2 py-1 rounded-full bg-emerald-100 shrink-0">
                                        <Check className="h-3.5 w-3.5" />
                                        {lang === "ar" ? "تم الحجز" : "Claimed"}
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => claim(o.id)}
                                        disabled={!!claimingId}
                                        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 disabled:opacity-50"
                                        data-testid={`signup-claim-${o.id}`}
                                    >
                                        {isClaiming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ticket className="h-3.5 w-3.5" />}
                                        {lang === "ar" ? "احجز" : "Claim"}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {error && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive mb-3">
                    {error}
                </div>
            )}

            <button
                type="button"
                onClick={onDone}
                className="w-full inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
                {claimedId
                    ? (lang === "ar" ? "إلى لوحة التحكم" : "Go to dashboard")
                    : (lang === "ar" ? "تخطّي الآن" : "Skip for now")}
            </button>
        </>
    );
}

// ── Main SignupPage ──────────────────────────────────────────────────────────
export default function SignupPage() {
    const { lang, isRTL } = useI18n();
    const navigate        = useNavigate();
    const [params]        = useSearchParams();

    const refCode = (() => {
        const fromUrl = (params.get("ref") || "").trim().toUpperCase();
        if (fromUrl) { localStorage.setItem("ref_code", fromUrl); return fromUrl; }
        return (localStorage.getItem("ref_code") || "").trim().toUpperCase();
    })();

    const [step, setStep]           = useState(0);
    const [signupData, setSignupData] = useState(null); // result from /auth/signup
    const [payRef, setPayRef]       = useState("");

    const handleSignupSuccess = (data) => {
        setSignupData(data);
        setStep(1);
    };

    const handleReferenceSuccess = (ref) => {
        setPayRef(ref);
        setStep(5);
    };

    const handleFirstDealDone = () => {
        navigate("/member/pending", { state: { payment_reference: payRef, member_code: signupData?.member_code } });
    };

    const stepContent = () => {
        switch (step) {
            case 0: return <Step0Account    lang={lang} refCode={refCode} onSuccess={handleSignupSuccess} />;
            case 1: return <Step1Identity   lang={lang} memberCode={signupData?.member_code} onNext={() => setStep(2)} />;
            case 2: return <Step2Interests  lang={lang} onNext={() => setStep(3)} onSkip={() => setStep(3)} />;
            case 3: return <Step3Payment    lang={lang} onNext={() => setStep(4)} />;
            case 4: return <Step4Reference  lang={lang} onSuccess={handleReferenceSuccess} />;
            case 5: return <Step5FirstDeal  lang={lang} onDone={handleFirstDealDone} />;
            default: return null;
        }
    };

    return (
        <div className="min-h-screen w-full flex" dir={isRTL ? "rtl" : "ltr"} data-testid="page-signup">

            {/* ── Left / Image panel ── */}
            <LeftPanel lang={lang} />

            {/* ── Right / Form panel ── */}
            <div className="relative flex flex-col w-full md:w-1/2 shrink-0 bg-background">

                {/* Top bar */}
                <div className="flex items-center justify-between px-8 pt-7">
                    {step > 0 && step < 5 ? (
                        <button
                            onClick={() => setStep((s) => s - 1)}
                            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {lang === "ar" ? "رجوع" : "Back"}
                        </button>
                    ) : step === 0 ? (
                        <a
                            href="/"
                            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {lang === "ar" ? "العودة إلى الموقع" : "Back to site"}
                        </a>
                    ) : (
                        <span />
                    )}
                    <LanguageToggle />
                </div>

                {/* Form area */}
                <div className="flex-1 flex flex-col justify-center px-8 sm:px-14 lg:px-20 py-10">
                    <div className="w-full max-w-md mx-auto">
                        {stepContent()}
                    </div>
                </div>

                <p className="text-center text-xs text-muted-foreground/40 pb-6 px-8">
                    © {new Date().getFullYear()} Hemaia Health Network
                </p>
            </div>

        </div>
    );
}
